import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import {
  useNotificationDevices,
  useNotificationPreferences,
} from "@/hooks/use-notifications";
import {
  notificationsApi,
  type PreferenceItem,
} from "@/services/notifications-api.service";

const SCOPE_ORDER = ["general", "billing", "security", "system", "marketing"] as const;

const SCOPE_LABELS: Record<string, string> = {
  general: "Новости платформы",
  billing: "Оплата / тарифы",
  security: "Безопасность",
  system: "Служебные",
  marketing: "Промо",
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationSettingsPage() {
  const { toast } = useToast();
  const prefsQuery = useNotificationPreferences();
  const devices = useNotificationDevices();
  const [local, setLocal] = useState<PreferenceItem[]>([]);
  const [vapid, setVapid] = useState("");

  useEffect(() => {
    if (prefsQuery.data) {
      const byScope = new Map(prefsQuery.data.map((p) => [p.scope, p]));
      setLocal(
        SCOPE_ORDER.map(
          (scope) =>
            byScope.get(scope) ?? {
              scope,
              inapp: true,
              email: true,
              push: false,
            },
        ),
      );
    }
  }, [prefsQuery.data]);

  useEffect(() => {
    void notificationsApi.pushVapid().then((r) => setVapid(r.vapid_public_key || ""));
  }, []);

  const dirty = useMemo(() => JSON.stringify(local) !== JSON.stringify(
    SCOPE_ORDER.map((scope) => {
      const found = prefsQuery.data?.find((p) => p.scope === scope);
      return found ?? { scope, inapp: true, email: true, push: false };
    }),
  ), [local, prefsQuery.data]);

  const update = (scope: string, key: "inapp" | "email" | "push", value: boolean) => {
    setLocal((prev) =>
      prev.map((p) => (p.scope === scope ? { ...p, [key]: value } : p)),
    );
  };

  const enablePush = async () => {
    try {
      if (!vapid) {
        toast({ title: "Web Push недоступен", description: "VAPID не настроен на сервере" });
        return;
      }
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast({ title: "Браузер не поддерживает Push", variant: "destructive" });
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast({ title: "Разрешение не выдано" });
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid) as BufferSource,
      });
      const json = sub.toJSON();
      await notificationsApi.registerDevice({
        endpoint: json.endpoint || sub.endpoint,
        p256dh: json.keys?.p256dh || "",
        auth: json.keys?.auth || "",
      });
      await devices.refetch();
      toast({ title: "Устройство зарегистрировано" });
    } catch (e) {
      toast({
        title: "Не удалось включить Push",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 space-y-4 p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/notifications" aria-label="К ленте">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            Настройки уведомлений
            <SectionHelpTooltip>
              <p>Матрица каналов по областям (scope).</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>in-app — лента и бейдж</li>
                <li>email / push — только при включённом канале и настроенных credentials</li>
                <li>Web Push: кнопка «Включить» регистрирует устройство</li>
              </ul>
            </SectionHelpTooltip>
          </h1>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Каналы по областям</CardTitle>
            <Button
              size="sm"
              disabled={!dirty || prefsQuery.save.isPending}
              onClick={() =>
                prefsQuery.save.mutate(local, {
                  onSuccess: () => toast({ title: "Сохранено" }),
                })
              }
            >
              Сохранить
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 text-xs text-muted-foreground">
              <span>Область</span>
              <span className="w-14 text-center">In-app</span>
              <span className="w-14 text-center">Email</span>
              <span className="w-14 text-center">Push</span>
            </div>
            {local.map((p) => (
              <div
                key={p.scope}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 border-b py-2"
              >
                <span className="text-sm">{SCOPE_LABELS[p.scope] ?? p.scope}</span>
                <div className="flex w-14 justify-center">
                  <Switch checked={p.inapp} onCheckedChange={(v) => update(p.scope, "inapp", v)} />
                </div>
                <div className="flex w-14 justify-center">
                  <Switch checked={p.email} onCheckedChange={(v) => update(p.scope, "email", v)} />
                </div>
                <div className="flex w-14 justify-center">
                  <Switch checked={p.push} onCheckedChange={(v) => update(p.scope, "push", v)} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Web Push устройства</CardTitle>
            <Button size="sm" variant="secondary" onClick={() => void enablePush()}>
              Включить уведомления
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {!vapid && (
              <p className="text-sm text-muted-foreground">
                VAPID-ключ на сервере не задан — Push недоступен (in-app работает).
              </p>
            )}
            {(devices.data ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">Нет зарегистрированных устройств</p>
            )}
            {(devices.data ?? []).map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded border px-3 py-2">
                <span className="truncate text-xs text-muted-foreground">{d.endpoint}</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => devices.remove.mutate(d.id)}
                >
                  Удалить
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
